import { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { gsap } from 'gsap';
import { styled } from '@mui/material/styles';
import { AnimatedIconProps, AnimatedIconRef } from './types';

const CheckmarkContainer = styled('div')<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: isVisible ? 1 : 0,
  transform: `scale(${isVisible ? 1 : 0.8})`,
  transition: theme.transitions.create(['opacity', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  width: isVisible ? '20px' : '0px',
  overflow: 'hidden',
}));

const CheckmarkSvg = styled('svg')(({ theme: _theme }) => ({
  flexShrink: 0,
  
  '& circle': {
    stroke: '#4CAF50', // Green color from the Lottie animation
    strokeWidth: 12, // Thicker stroke
    strokeDasharray: 283,
    strokeDashoffset: 283,
  },
  
  '& path': {
    stroke: '#4CAF50', // Green color from the Lottie animation
    strokeWidth: 12, // Thicker stroke
    strokeDasharray: 50,
    strokeDashoffset: 50,
  },
}));

export const AniCheckmarkIcon = forwardRef<AnimatedIconRef, AnimatedIconProps>(({
  animationDuration = 0.6,
  isVisible = false,
  onAnimationStart,
  onAnimationComplete,
  size = 20,
}, ref) => {
  const checkmarkRef = useRef<SVGSVGElement>(null);
  const circleRef = useRef<SVGCircleElement>(null);
  const checkRef = useRef<SVGPathElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCompleted, setIsCompleted] = useState(isVisible);

  const animate = () => {
    if (!checkmarkRef.current || !circleRef.current || !checkRef.current || isAnimating) return;
    
    setIsAnimating(true);
    onAnimationStart?.();
    
    const tl = gsap.timeline({
      onComplete: () => {
        setIsAnimating(false);
        setIsCompleted(true);
        onAnimationComplete?.();
      }
    });

    // Reset initial state
    gsap.set(circleRef.current, { 
      strokeDasharray: 283, 
      strokeDashoffset: 283,
      rotation: -90,
      transformOrigin: "center"
    });
    
    gsap.set(checkRef.current, { 
      strokeDasharray: 50, 
      strokeDashoffset: 50 
    });

    // Animate circle drawing
    tl.to(circleRef.current, {
      strokeDashoffset: 0,
      duration: animationDuration * 0.7,
      ease: "power2.out"
    })
    // Animate checkmark drawing
    .to(checkRef.current, {
      strokeDashoffset: 0,
      duration: animationDuration * 0.3,
      ease: "power2.out"
    }, "-=0.1")
    // Scale effect
    .to(checkmarkRef.current, {
      scale: 1.1,
      duration: 0.1,
      ease: "power2.out"
    })
    .to(checkmarkRef.current, {
      scale: 1,
      duration: 0.1,
      ease: "power2.out"
    });
  };

  const reset = () => {
    if (!checkmarkRef.current || !circleRef.current || !checkRef.current) return;
    
    gsap.set(circleRef.current, { 
      strokeDashoffset: 283 
    });
    gsap.set(checkRef.current, { 
      strokeDashoffset: 50 
    });
    gsap.set(checkmarkRef.current, { 
      scale: 1 
    });
    
    setIsCompleted(false);
    setIsAnimating(false);
  };

  useImperativeHandle(ref, () => ({
    animate,
    reset,
    isAnimating,
  }));

  return (
    <CheckmarkContainer 
      isVisible={isCompleted || isVisible || isAnimating}
      sx={{
        width: size,
        height: size,
      }}
    >
      <CheckmarkSvg
        ref={checkmarkRef}
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
      >
        <circle
          ref={circleRef}
          cx="50"
          cy="50"
          r="45"
          fill="none"
          strokeLinecap="round"
        />
        <path
          ref={checkRef}
          d="M30 50L45 65L70 35"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </CheckmarkSvg>
    </CheckmarkContainer>
  );
});

AniCheckmarkIcon.displayName = 'AniCheckmarkIcon';
